import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import RecruitingVideosModal from './components/RecruitingVideosModal';
import VideoLinkList from './components/VideoLinkList';
import Img from '../../../Img';
import { PowerFunnelGatedFeatures } from '../../../Analytics/constants';
import useCollegeProspectAccess from '../../../hooks/useCollegeProspectAccess';
import { getApiAxiosInstance } from '../../../../utils/network';
import { showErrorPopup } from '../../../../actions/notification-actions';
import { RECRUITING_VIDEOS_ENDPOINT } from './constants';
import styles from '../collegeProspectTab.scss';

const TESTID_PREFIX = 'profile.college-prospect.recruiting-videos';
const EMPTY_STATE_COPY = 'Add up to 4 videos to share with coaches.';

const CollegeRecruitingVideos = () => {
    const dispatch = useDispatch();
    const profileData = useSelector((state) => state.profile?.data) || {};
    const authUser = useSelector((state) => state.auth?.user);
    const isOwnProfile = !!authUser && !!profileData.memberId && authUser.id === profileData.memberId;
    const { hasAnnualPower, openUpgradePopup } = useCollegeProspectAccess();
    const isLocked = !hasAnnualPower;

    const [videos, setVideos] = useState([]);
    const [isEditing, setIsEditing] = useState(false);

    const fetchVideos = useCallback(async () => {
        try {
            const response = await getApiAxiosInstance().get(RECRUITING_VIDEOS_ENDPOINT);
            setVideos(response.data?.items || []);
        } catch (err) {
            dispatch(showErrorPopup({
                message: 'We couldn\'t load your videos. Please try again.',
                error: err,
            }));
            setVideos([]);
        }
    }, [dispatch]);

    useEffect(() => {
        if (!isOwnProfile || !hasAnnualPower) {
            return;
        }
        fetchVideos();
    }, [isOwnProfile, hasAnnualPower, fetchVideos]);

    const handleEditClick = () => {
        if (isLocked) {
            openUpgradePopup(PowerFunnelGatedFeatures.CPP_RECRUITING_VIDEOS_LOCKED);
            return;
        }
        setIsEditing(true);
    };

    const handleLockKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleEditClick();
        }
    };

    const handleSaved = async () => {
        setIsEditing(false);
        try {
            const response = await getApiAxiosInstance().get(RECRUITING_VIDEOS_ENDPOINT);
            setVideos(response.data?.items || []);
        } catch {
            setVideos([]);
        }
    };

    return (
        <div
            className={styles.academicsSection}
            data-testid={`${TESTID_PREFIX}.container`}
        >
            <div className={styles.academicsHeader}>
                <div className={styles.academicsTitleBlock}>
                    <div
                        className={styles.academicsTitle}
                        data-testid={`${TESTID_PREFIX}.title`}
                    >
                        Video
                    </div>
                    <div
                        className={styles.academicsSubtitle}
                        data-testid={`${TESTID_PREFIX}.subtitle`}
                    >
                        Viewable by college coaches.
                    </div>
                </div>
                {isOwnProfile && (
                    <button
                        type="button"
                        className={styles.academicsEditBtn}
                        onClick={handleEditClick}
                        data-testid={`${TESTID_PREFIX}.edit-btn`}
                    >
                        <Img
                            src="/img/v4/pen.svg"
                            alt=""
                            className={styles.academicsEditIcon}
                        />
                        <span data-testid={`${TESTID_PREFIX}.edit-btn.text`}>Edit</span>
                    </button>
                )}
            </div>

            <div className={styles.academicsCard}>
                <div className={styles.academicsBody}>
                    {!isLocked && videos.length > 0 && (
                        <VideoLinkList
                            videos={videos}
                            data-testid={TESTID_PREFIX}
                        />
                    )}
                    {(isLocked || videos.length === 0) && (
                        <div
                            className={styles.recruitingVideosEmptyText}
                            data-testid={`${TESTID_PREFIX}.empty-text`}
                        >
                            {EMPTY_STATE_COPY}
                        </div>
                    )}
                    {isLocked && (
                        <div
                            className={styles.sectionLockRow}
                            role="button"
                            tabIndex={0}
                            onClick={handleEditClick}
                            onKeyDown={handleLockKeyDown}
                            data-testid={`${TESTID_PREFIX}.lock`}
                        >
                            <span className={styles.socialLockBox}>
                                <Img
                                    src="/img/v4/lock.svg"
                                    alt="Locked"
                                    className={styles.socialLockIcon}
                                />
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {isEditing && isOwnProfile && hasAnnualPower && (
                <RecruitingVideosModal
                    initialVideos={videos}
                    onClose={() => setIsEditing(false)}
                    onSaved={handleSaved}
                />
            )}
        </div>
    );
};

export default CollegeRecruitingVideos;
